import { useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@/routeTree.gen'

/**
 * The pages the cookie consent banner is not allowed to appear on.
 *
 * The cookies policy, the privacy policy and the terms of use — pages a visitor is reading rather than
 * acting on, and the banner is a `position: fixed` bar over the bottom strip, so it sits on top of the
 * text. They are also where the banner's own "Cookies policy" link leads, which makes covering the page
 * the expected outcome of following it rather than an unlucky one — the cookies policy most of all, since
 * it is the page a visitor opens precisely to work out what they are being asked.
 *
 * Suppressing it there costs nothing: nothing is consented to in the meantime, and the banner is due again
 * the moment they leave for a page that actually processes their data. The preferences are still reachable
 * from every one of these pages — the footer's "Cookie settings" opens the dialog, which is the surface
 * that can actually answer the question, and per category rather than all or nothing. Only the bar is
 * withheld. See `CookieBanner`.
 *
 * Route ids rather than paths, typed as the generated ids, so renaming a route file breaks the build here
 * instead of quietly re-admitting the banner.
 */
const BANNER_FREE_ROUTE_IDS: readonly FileRouteTypes['id'][] = [
  '/_landing/cookies-policy',
  '/_landing/privacy-policy',
  '/_landing/terms-of-use',
]

/**
 * Whether the page currently rendered is one of {@link BANNER_FREE_ROUTE_IDS}.
 *
 * Read from the router's matches rather than from the URL, so it follows client-side navigation: entering
 * one of these pages hides the banner and leaving brings it back, with no state of its own to keep in
 * step. Only consumed inside `ClientOnly` (that is where the banner mounts), so it cannot make the server
 * and the client disagree about what to render.
 */
export function useBannerFreeRoute(): boolean {
  return useRouterState({
    select: (state) => state.matches.some((match) => BANNER_FREE_ROUTE_IDS.some((id) => id === match.routeId)),
  })
}
